export const blogPosts = [
  {
    id: 1,
    title: "The Complete Guide to Protein: Why It's Essential for Muscle Building",
    excerpt: "Understanding how protein works in your body and how much you need to maximize muscle growth and recovery.",
    content: `
      <h2>Why Protein is the Building Block of Muscles</h2>
      
      <p>Protein is often called the "building block of life" for good reason. When it comes to building muscle, protein plays a crucial role that no other macronutrient can match.</p>
      
      <h3>What is Protein?</h3>
      <p>Proteins are large molecules made up of amino acids, which are the basic units that your body uses to build and repair tissues. There are 20 different amino acids, and your body needs all of them to function properly.</p>
      
      <h3>How Protein Builds Muscle</h3>
      <p>When you work out, you create tiny tears in your muscle fibers. This is actually a good thing! Your body then uses protein to repair these tears, making the muscles stronger and larger than before. This process is called muscle protein synthesis.</p>
      
      <h3>How Much Protein Do You Need?</h3>
      <p>For muscle building, the general recommendation is:</p>
      <ul>
        <li><strong>Sedentary adults:</strong> 0.8g per kg of body weight</li>
        <li><strong>Active adults:</strong> 1.2-1.4g per kg of body weight</li>
        <li><strong>Strength training:</strong> 1.6-2.2g per kg of body weight</li>
      </ul>
      
      <h3>Best Protein Sources</h3>
      <p>Not all proteins are created equal. Here are the best sources:</p>
      <ul>
        <li><strong>Lean meats:</strong> Chicken breast, turkey, lean beef</li>
        <li><strong>Fish:</strong> Salmon, tuna, cod</li>
        <li><strong>Eggs:</strong> Complete protein with all essential amino acids</li>
        <li><strong>Dairy:</strong> Greek yogurt, cottage cheese, milk</li>
        <li><strong>Plant-based:</strong> Quinoa, lentils, chickpeas, tofu</li>
      </ul>
      
      <h3>When to Eat Protein</h3>
      <p>Timing matters! The best times to consume protein are:</p>
      <ul>
        <li><strong>Within 30 minutes after workout:</strong> This is when your muscles are most receptive to protein</li>
        <li><strong>Every 3-4 hours:</strong> Spread protein intake throughout the day</li>
        <li><strong>Before bed:</strong> Casein protein provides slow-release amino acids during sleep</li>
      </ul>
      
      <h3>Common Myths Debunked</h3>
      <p><strong>Myth:</strong> "You can only absorb 30g of protein at once"<br>
      <strong>Fact:</strong> Your body can absorb much more, but spreading it out is still better for muscle building.</p>
      
      <p><strong>Myth:</strong> "Plant proteins are incomplete"<br>
      <strong>Fact:</strong> While some plant proteins are lower in certain amino acids, combining different plant sources can provide complete protein.</p>
      
      <h3>Practical Tips</h3>
      <p>Here are some practical ways to increase your protein intake:</p>
      <ul>
        <li>Add protein powder to smoothies or oatmeal</li>
        <li>Snack on Greek yogurt or cottage cheese</li>
        <li>Include protein in every meal</li>
        <li>Prepare protein-rich meals in advance</li>
        <li>Use protein-rich ingredients in recipes (add beans to salads, eggs to rice dishes)</li>
      </ul>
      
      <h3>Conclusion</h3>
      <p>Protein is essential for muscle building, but it's not magic. You need to combine adequate protein intake with proper training, rest, and overall nutrition. Start by calculating your protein needs, then gradually increase your intake while monitoring your progress.</p>
      
      <p>Remember, consistency is key. Small changes in your protein intake, maintained over time, will lead to significant improvements in your muscle mass and strength.</p>
    `,
    author: "Dr. Sarah Johnson",
    authorTitle: "Sports Nutritionist",
    date: "2024-01-15",
    category: "food",
    image: "/assets/blog/protein-meal.png",
    readTime: "8 min read",
    tags: ["protein", "muscle building", "nutrition", "fitness"]
  },
  {
    id: 2,
    title: "5 Essential Cardio Exercises for Beginners",
    excerpt: "Start your cardio journey with these beginner-friendly exercises that will boost your endurance and heart health.",
    content: `
      <h2>Getting Started with Cardio</h2>
      <p>Cardiovascular exercise is essential for overall health and fitness. Here are 5 beginner-friendly cardio exercises to get you started.</p>
      
      <h3>1. Walking</h3>
      <p>Walking is the most natural form of cardio and perfect for beginners. Start with 20-30 minutes at a comfortable pace and gradually increase duration and intensity.</p>
      
      <h3>2. Cycling</h3>
      <p>Whether on a stationary bike or outdoors, cycling is low-impact and great for building leg strength and cardiovascular endurance.</p>
      
      <h3>3. Swimming</h3>
      <p>Swimming provides a full-body workout while being gentle on your joints. It's excellent for building endurance and muscle tone.</p>
      
      <h3>4. Jump Rope</h3>
      <p>Jumping rope is a high-intensity cardio exercise that improves coordination, balance, and cardiovascular fitness.</p>
      
      <h3>5. Dancing</h3>
      <p>Dancing is a fun way to get your heart rate up while improving coordination and flexibility.</p>
    `,
    author: "Mike Chen",
    authorTitle: "Personal Trainer",
    date: "2024-01-10",
    category: "cardio",
    image: "/assets/blog/cardio.png",
    readTime: "5 min read",
    tags: ["cardio", "beginners", "endurance", "health"]
  },
  {
    id: 3,
    title: "How to Choose the Right Workout Clothes",
    excerpt: "Your workout gear can make or break your training session. Learn how to select the perfect athletic wear for your needs.",
    content: `
      <h2>Selecting the Perfect Workout Gear</h2>
      <p>The right workout clothes can enhance your performance and comfort during exercise. Here's what to consider when choosing your athletic wear.</p>
      
      <h3>Fabric Matters</h3>
      <p>Look for moisture-wicking fabrics like polyester, nylon, or merino wool. These materials help keep you dry and comfortable during intense workouts.</p>
      
      <h3>Fit and Comfort</h3>
      <p>Your workout clothes should fit well without being too tight or too loose. They should allow for full range of motion without restricting movement.</p>
      
      <h3>Activity-Specific Gear</h3>
      <p>Different activities require different types of clothing. Running shorts are different from yoga pants, and each serves a specific purpose.</p>
    `,
    author: "Emma Rodriguez",
    authorTitle: "Fashion & Fitness Expert",
    date: "2024-01-08",
    category: "clothes",
    image: "/assets/blog/workout_clothes.png",
    readTime: "6 min read",
    tags: ["workout gear", "comfort", "performance", "fashion"]
  }
]

export const categories = [
  { id: 'all', name: 'allCategories', icon: '📚' },
  { id: 'app', name: 'app', icon: '📱' },
  { id: 'cardio', name: 'cardio', icon: '❤️' },
  { id: 'clothes', name: 'clothes', icon: '👕' },
  { id: 'definition', name: 'definition', icon: '💪' },
  { id: 'food', name: 'food', icon: '🍎' },
  { id: 'tips', name: 'tips', icon: '💡' },
  { id: 'science', name: 'science', icon: '🔬' },
  { id: 'workoutPlans', name: 'workoutPlans', icon: '📋' },
  { id: 'exercises', name: 'exercises', icon: '🏋️' }
]
